import { BaseQueryFn, FetchBaseQueryError } from '@reduxjs/toolkit/query/react';
import Cookies from 'js-cookie';
import { getEnv } from '../../components/Context/env';
import { extractSnowplowCookieValues } from '../../helpers';

type HttpMethod = 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';

export type ProxyQueryArgs = {
  url: string;
  method?: HttpMethod;
  body?: unknown;
};

type AuthState = {
  auth?: {
    csrfToken?: string;
    token?: string;
    sessionId?: string;
    currentUser?: {
      customerId?: string;
    };
  };
  analyticsPlatform?: {
    channel?: string | null;
  };
};

const defaultHeaders: Record<string, string> = {
  'Content-Type': 'application/json',
  'x-brand': 'creditmatcher',
  'x-api-version': '3.0.0',
  'x-clientid': 'web_prodmove',
};

const additionalHeaders = {
  DATA_USER: 'x-data-user',
  DATA_SESSION: 'x-data-session',
  CHANNEL: 'x-channel',
};

const proxyBaseQuery: BaseQueryFn<ProxyQueryArgs, unknown, FetchBaseQueryError> = async (
  { url, method = 'GET', body },
  { getState, signal }
) => {
  const env = getEnv();
  const { auth, analyticsPlatform } = getState() as AuthState;
  const gatewayUrl = env.REACT_APP_GATEWAY_URL;
  const requestedUrl = `${gatewayUrl}${url}`;
  const { domainUserId, domainSessionId } = extractSnowplowCookieValues(Cookies.get());

  const internalUrl = `/internal/proxy?for=${requestedUrl}&method=${method}`;

  const headers: Record<string, string> = {
    ...defaultHeaders,
    'CSRF-Token': auth?.csrfToken ?? '',
    ...(domainUserId && { [additionalHeaders.DATA_USER]: domainUserId }),
    ...(domainSessionId && { [additionalHeaders.DATA_SESSION]: domainSessionId }),
    ...(analyticsPlatform?.channel != null && { [additionalHeaders.CHANNEL]: analyticsPlatform.channel }),
  };

  const proxiedHeaders: Record<string, string> = { ...headers };
  if (auth?.token) {
    proxiedHeaders.Authorization = `Bearer ${auth.token}`;
  }

  if (auth?.currentUser?.customerId) {
    proxiedHeaders['x-customerId'] = auth.currentUser.customerId;
  }

  try {
    const response = await fetch(internalUrl, {
      method: 'POST',
      signal,
      headers: new Headers(headers),
      body: JSON.stringify({
        requestedUrl,
        appName: 'Product Movement',
        requestTime: new Date(),
        sessionId: auth?.sessionId,
        method,
        body,
        headers: proxiedHeaders,
        meta: {},
      }),
    });

    if (!response.ok) {
      return { error: { status: response.status, data: await response.text() } as FetchBaseQueryError };
    }

    const data = response.status === 204 ? null : await response.json();
    return { data };
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error);
    return { error: { status: 'FETCH_ERROR', error: message } as FetchBaseQueryError };
  }
};

export default proxyBaseQuery;
